/**
 * @author       justin.jeong@zumper.com (Justin Jeong)
 * @copyright    Copyright (c) 2022, Zumper
 */

import { type AnalyticsC, type Origin, localStorageSingleton } from '@zcode/nr-utils'
import {
  type APPLY,
  type MESSAGE,
  MESSAGE_FORM_MODAL,
  type TOUR,
  type WAITLIST,
} from '@zcode/nr/es/shared/constants/message'
import type {
  FloorplanListing,
  Listable,
  ListingData,
  Property,
  SendMessageArgsSingle,
} from '@zcode/state-type'
import { format } from 'date-fns'
import type { Dispatch } from 'redux'
import { getRefreshedMoveInDate } from '../../modules/profile/thunks/getRefreshedMoveInDate'
import { checkDateValidity } from '../../packages/Inputs/validator/moveIn'
import { getCustomMessagesByIntent } from '../message/MessageForm/helpers/getCustomMessagesByIntent'
import { oneTapDataConflictsWithLoggedInUser } from './oneTapDataConflictsWithLoggedInUser'

interface MessageData {
  analyticChannel:
    | typeof AnalyticsC.AFFORDABILITY_CALCULATOR
    | typeof AnalyticsC.FULL_DETAIL
    | typeof AnalyticsC.LIST
    | typeof AnalyticsC.LIST_ITEM
    | typeof AnalyticsC.LIST_ITEM_MESSAGED
    | typeof AnalyticsC.PERMANENT_ADDRESS
    | typeof AnalyticsC.PROGRAMMATIC_RENT
    | typeof AnalyticsC.HOMEPAGE
  bedroomsArray: readonly boolean[]
  disableOneTap?: boolean
  entity: Listable | Property | null | undefined
  equalCTAIntent?: typeof APPLY | typeof MESSAGE | typeof TOUR | typeof WAITLIST
  origin: Origin
  selectedFloorplan?: FloorplanListing
}

export const getFPMatchingPreferences = (
  entity: Listable | Property,
  bedArray: readonly boolean[],
  savedFP: ListingData | undefined
) => {
  const minBedroom = bedArray?.findIndex?.((b) => !!b)
  const maxBedroom = bedArray?.lastIndexOf?.(true)
  return entity.activeFloorplans?.find?.((fp) => {
    if (minBedroom >= 0 && maxBedroom >= 0) {
      // we have filters set on the page. Use this value to find a matching floor plan
      return fp.bedrooms >= minBedroom && fp.bedrooms <= maxBedroom
    } else if (savedFP) {
      // use the most recently messaged floor plan data
      return fp.bedrooms === savedFP.bedrooms
    }
    return undefined
  })
}

export const getOneTapMessageData =
  (messageData: MessageData) =>
  async (dispatch: Dispatch): Promise<SendMessageArgsSingle | null> => {
    const {
      analyticChannel,
      disableOneTap,
      entity,
      equalCTAIntent,
      origin,
      selectedFloorplan,
      bedroomsArray,
    } = messageData
    //  the following will only exist once the user sends a lead
    const savedOneTapFormInputs: {
      email?: string
      floorplan?: ListingData
      moveIn: string | null
      name?: string
      phone?: string | null
    } = localStorageSingleton.get('one_tap_form_inputs')

    if (
      disableOneTap ||
      !entity ||
      entity.isCustomMessageRequired ||
      !savedOneTapFormInputs ||
      (!savedOneTapFormInputs.phone && entity.isPhoneRequired) ||
      dispatch(oneTapDataConflictsWithLoggedInUser(savedOneTapFormInputs))
    ) {
      // we have no valid entity or no saved inputs
      return null
    }
    if (!checkDateValidity(savedOneTapFormInputs.moveIn)) {
      // the movein is no longer valid
      const { refreshedMoveInDate } = await dispatch(getRefreshedMoveInDate())
      if (refreshedMoveInDate && checkDateValidity(refreshedMoveInDate)) {
        savedOneTapFormInputs.moveIn = format(refreshedMoveInDate, 'MM/dd/yyyy')
      } else {
        return null
      }
    }

    const { email, floorplan: savedFP, moveIn, name, phone } = savedOneTapFormInputs

    const fpMatchingPreferenences = getFPMatchingPreferences(entity, bedroomsArray, savedFP)

    if (entity.isBuilding && !selectedFloorplan) {
      // we're messaging a building, but no specified floor plan is given
      if (!fpMatchingPreferenences) {
        // we weren't able to find a floor plan matching our previous choice
        return null
      }
    }
    const { headerText } = entity

    return {
      analyticChannel,
      entity,
      equalCTAIntent,
      floorplan: selectedFloorplan || fpMatchingPreferenences,
      formType: MESSAGE_FORM_MODAL,
      oneTap: true,
      selectedFloorplanId: selectedFloorplan?.id || fpMatchingPreferenences?.id,
      messageData: {
        email,
        messageModified: false,
        moveIn,
        msg: getCustomMessagesByIntent({
          listingName: headerText,
        }),
        name,
        origin,
        phone,
      },
    }
  }
